import React, { useEffect, useMemo, useState } from 'react';
import { Card } from './ui/Card';
import { Input } from './ui/Input';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { SearchIcon, BriefcaseIcon, MessageCircleIcon, StarIcon, TrendingUpIcon, UsersIcon, CalendarIcon, XIcon, CheckIcon, ClockIcon, UserIcon, MailIcon, MapPinIcon, FilterIcon, ArrowLeftIcon, SendIcon, RefreshCwIcon, CalendarDaysIcon } from 'lucide-react';
import { useEvent } from '../contexts/EventContext';
import { useMySchedule } from '../contexts/MyScheduleContext';
import { Attendee, Meeting, AvailabilitySlot } from '../types';
import { attendees as attendeesData } from '../data/attendees';
interface ChatMessage {
  id: string;
  senderId: string;
  text: string;
  timestamp: string;
  isMe: boolean;
}
interface ChatConversation {
  id: string;
  attendeeId: string;
  attendeeName: string;
  attendeeRole?: string;
  attendeeCompany?: string;
  attendeeImage?: string;
  lastMessage: string;
  lastMessageTime: string;
  unreadCount: number;
  messages: ChatMessage[];
}
interface NetworkingProps {
  sessions?: any[];
  exhibitors?: any[];
  onNavigateToSchedule?: () => void;
  initialMeetingId?: string;
  cameFromSchedule?: boolean;
  onUpdateHeader?: (title: string, subtitle?: string) => void;
  onNavigateBack?: () => void;
}
export function Networking({
  sessions: propSessions = [],
  exhibitors: propExhibitors = [],
  onNavigateToSchedule,
  initialMeetingId,
  cameFromSchedule = false,
  onUpdateHeader,
  onNavigateBack
}: NetworkingProps) {
  const {
    currentEvent
  } = useEvent();
  const {
    meetings,
    addMeeting,
    updateMeeting,
    removeMeeting
  } = useMySchedule();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState<'matches' | 'attendees' | 'meetings' | 'chats'>('matches');
  const [currentView, setCurrentView] = useState<'main' | 'profile' | 'chat' | 'meeting' | 'meetingDetails' | 'reschedule'>('main');
  // Navigation stack to track view history
  // Start with empty stack if coming from schedule with a meeting ID
  const [navigationStack, setNavigationStack] = useState<Array<{
    view: string;
    data?: any;
  }>>([]);
  const [selectedAttendee, setSelectedAttendee] = useState<Attendee | null>(null);
  const [selectedChat, setSelectedChat] = useState<ChatConversation | null>(null);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('October 24, 2025');
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [messageInput, setMessageInput] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  // Add day filter state
  const [selectedDay, setSelectedDay] = useState('all');
  // Filter states
  const [selectedSeniority, setSelectedSeniority] = useState<string>('all');
  const [selectedInterest, setSelectedInterest] = useState<string>('all');
  const [selectedResponsibility, setSelectedResponsibility] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  // Update header based on current view
  useEffect(() => {
    if (!onUpdateHeader) return;
    switch (currentView) {
      case 'main':
        onUpdateHeader('Networking', 'Connect with industry professionals');
        break;
      case 'profile':
        onUpdateHeader('Profile', selectedAttendee?.name);
        break;
      case 'chat':
        onUpdateHeader('Chat', selectedChat?.attendeeName);
        break;
      case 'meeting':
        onUpdateHeader('Request Meeting', selectedAttendee?.name);
        break;
      case 'meetingDetails':
        onUpdateHeader('Meeting Details');
        break;
      case 'reschedule':
        onUpdateHeader('Reschedule Meeting');
        break;
    }
  }, [currentView, selectedAttendee, selectedChat, onUpdateHeader]);
  // Handle back navigation - returns true if should exit Networking
  const handleInternalBack = (): boolean => {
    if (navigationStack.length === 0) {
      // No internal history, should exit networking
      return true;
    }
    // Pop current view and go to previous
    const newStack = [...navigationStack];
    newStack.pop();
    if (newStack.length === 0) {
      // Popped the last item, should exit networking
      return true;
    }
    const previousView = newStack[newStack.length - 1];
    setNavigationStack(newStack);
    setCurrentView(previousView.view as any);
    // Restore state from previous view
    if (previousView.data) {
      if (previousView.data.attendee) setSelectedAttendee(previousView.data.attendee);
      if (previousView.data.chat) setSelectedChat(previousView.data.chat);
      if (previousView.data.meeting) setSelectedMeeting(previousView.data.meeting);
    }
    // Return false to indicate we handled the back internally
    return false;
  };
  // Expose back handler to parent
  useEffect(() => {
    window.__networkingBackHandler = handleInternalBack;
    return () => {
      delete window.__networkingBackHandler;
    };
  }, [navigationStack, currentView]);
  // Push new view to navigation stack
  const pushView = (view: string, data?: any) => {
    setNavigationStack([...navigationStack, {
      view,
      data
    }]);
    setCurrentView(view as any);
  };
  // Use prop sessions directly
  const sessions = propSessions;
  // Handle initial meeting ID navigation
  useEffect(() => {
    if (initialMeetingId && navigationStack.length === 0) {
      const meeting = meetings.find(m => m.id === initialMeetingId);
      if (meeting) {
        setSelectedTab('meetings');
        // Create attendee from meeting
        const attendeeFromMeeting: Attendee = {
          id: meeting.attendeeId,
          name: meeting.attendeeName,
          role: meeting.attendeeRole,
          company: meeting.attendeeCompany,
          interests: [],
          source: 'meeting'
        };
        setSelectedAttendee(attendeeFromMeeting);
        setSelectedMeeting(meeting);
        // Go directly to meeting details without adding 'main' to stack
        setNavigationStack([{
          view: 'meetingDetails',
          data: {
            meeting,
            attendee: attendeeFromMeeting
          }
        }]);
        setCurrentView('meetingDetails');
      }
    } else if (navigationStack.length === 0) {
      // Initialize with main view if no initial meeting
      setNavigationStack([{
        view: 'main'
      }]);
    }
  }, [initialMeetingId, meetings]);
  // Helper function to determine seniority from job title
  const getSeniorityFromTitle = (title: string): string => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('founder') || lowerTitle.includes('owner')) {
      return 'Founder/Owner';
    }
    if (lowerTitle.includes('ceo') || lowerTitle.includes('chief') || lowerTitle.includes('president')) {
      return 'C-Suite';
    }
    if (lowerTitle.includes('director') || lowerTitle.includes('vp')) {
      return 'Director/VP';
    }
    if (lowerTitle.includes('head')) {
      return 'Head';
    }
    if (lowerTitle.includes('manager')) {
      return 'Manager';
    }
    return 'Executive';
  };
  // Helper function to extract responsibility from job title
  const getResponsibilityFromTitle = (title: string): string => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('sales')) return 'Sales';
    if (lowerTitle.includes('marketing')) return 'Marketing';
    if (lowerTitle.includes('operations')) return 'Operations';
    if (lowerTitle.includes('product')) return 'Product';
    if (lowerTitle.includes('business development')) return 'Business Development';
    if (lowerTitle.includes('finance')) return 'Finance';
    if (lowerTitle.includes('technology') || lowerTitle.includes('tech')) return 'Technology';
    return 'General Management';
  };
  const attendees = attendeesData;
  // Mock chat conversations
  const [chats, setChats] = useState<ChatConversation[]>([{
    id: 'chat-1',
    attendeeId: attendees[1]?.id || '2',
    attendeeName: attendees[1]?.name || 'Michael Johnson',
    attendeeRole: attendees[1]?.role,
    attendeeCompany: attendees[1]?.company,
    attendeeImage: attendees[1]?.profileImage,
    lastMessage: 'Looking forward to connecting at the event!',
    lastMessageTime: '2 hours ago',
    unreadCount: 2,
    messages: [{
      id: 'msg-1',
      senderId: attendees[1]?.id || '2',
      text: 'Hi! Great to connect with you.',
      timestamp: '10:30 AM',
      isMe: false
    }, {
      id: 'msg-2',
      senderId: 'me',
      text: 'Thanks! Excited to meet at the event.',
      timestamp: '10:35 AM',
      isMe: true
    }, {
      id: 'msg-3',
      senderId: attendees[1]?.id || '2',
      text: 'Looking forward to connecting at the event!',
      timestamp: '11:20 AM',
      isMe: false
    }]
  }, {
    id: 'chat-2',
    attendeeId: attendees[2]?.id || '3',
    attendeeName: attendees[2]?.name || 'Emma Williams',
    attendeeRole: attendees[2]?.role,
    attendeeCompany: attendees[2]?.company,
    attendeeImage: attendees[2]?.profileImage,
    lastMessage: 'Thanks for the introduction!',
    lastMessageTime: '1 day ago',
    unreadCount: 0,
    messages: [{
      id: 'msg-4',
      senderId: 'me',
      text: 'Hi Emma, saw your profile and thought we should connect.',
      timestamp: 'Yesterday 3:00 PM',
      isMe: true
    }, {
      id: 'msg-5',
      senderId: attendees[2]?.id || '3',
      text: 'Thanks for the introduction!',
      timestamp: 'Yesterday 3:15 PM',
      isMe: false
    }]
  }]);
  const topMatches = attendees.slice(0, 10);
  // Enhanced filtering with seniority, interests, and responsibilities
  const filteredAttendees = attendees.filter(attendee => {
    const matchesSearch = attendee.name.toLowerCase().includes(searchQuery.toLowerCase()) || attendee.company?.toLowerCase().includes(searchQuery.toLowerCase()) || attendee.role?.toLowerCase().includes(searchQuery.toLowerCase());
    const attendeeSeniority = getSeniorityFromTitle(attendee.role || '');
    const matchesSeniority = selectedSeniority === 'all' || attendeeSeniority === selectedSeniority;
    const matchesInterest = selectedInterest === 'all' || attendee.interests.some(interest => interest.toLowerCase().includes(selectedInterest.toLowerCase()));
    const attendeeResponsibility = getResponsibilityFromTitle(attendee.role || '');
    const matchesResponsibility = selectedResponsibility === 'all' || attendeeResponsibility === selectedResponsibility;
    return matchesSearch && matchesSeniority && matchesInterest && matchesResponsibility;
  });
  // Seniority categories for filter
  const seniorityCategories = ['all', 'Founder/Owner', 'C-Suite', 'Director/VP', 'Head', 'Manager', 'Executive'];
  // Get unique interests for filter
  const interestOptions = useMemo(() => {
    const allInterests = new Set<string>();
    attendees.forEach(a => a.interests.forEach(i => allInterests.add(i)));
    return ['all', ...Array.from(allInterests).sort()];
  }, [attendees]);
  // Responsibility options for filter
  const responsibilityOptions = ['all', 'Sales', 'Marketing', 'Operations', 'Product', 'Business Development', 'Finance', 'Technology', 'General Management'];
  // Generate time slots (30-min intervals)
  const timeSlots = useMemo(() => {
    const slots: string[] = [];
    for (let hour = 9; hour < 18; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      slots.push(`${hour.toString().padStart(2, '0')}:30`);
    }
    return slots;
  }, []);
  const eventDates = ['October 24, 2025', 'October 25, 2025', 'October 26, 2025'];
  // Mock user availability (in real app, this would come from profile)
  const userAvailability: AvailabilitySlot[] = [{
    date: 'October 24, 2025',
    startTime: '09:00',
    endTime: '17:00',
    blockedSlots: ['10:00', '10:30', '15:00', '15:30']
  }, {
    date: 'October 25, 2025',
    startTime: '09:00',
    endTime: '17:00',
    blockedSlots: ['14:00', '14:30']
  }, {
    date: 'October 26, 2025',
    startTime: '10:00',
    endTime: '16:00',
    blockedSlots: []
  }];
  // Mock attendee availability
  const getAttendeeAvailability = (attendeeId: string): AvailabilitySlot[] => {
    // In real app, fetch from attendee profile
    return [{
      date: 'October 24, 2025',
      startTime: '09:00',
      endTime: '17:00',
      blockedSlots: ['11:00', '11:30', '14:00', '14:30']
    }, {
      date: 'October 25, 2025',
      startTime: '10:00',
      endTime: '16:00',
      blockedSlots: ['13:00', '13:30']
    }, {
      date: 'October 26, 2025',
      startTime: '09:00',
      endTime: '17:00',
      blockedSlots: ['10:00', '10:30', '15:00', '15:30']
    }];
  };
  const getSlotAvailability = (date: string, slot: string) => {
    const userDay = userAvailability.find(a => a.date === date);
    const attendeeDay = selectedAttendee ? getAttendeeAvailability(selectedAttendee.id).find(a => a.date === date) : null;
    if (!userDay || !attendeeDay) return 'unavailable';
    const slotHour = parseInt(slot.split(':')[0]);
    const userStartHour = parseInt(userDay.startTime.split(':')[0]);
    const userEndHour = parseInt(userDay.endTime.split(':')[0]);
    const attendeeStartHour = parseInt(attendeeDay.startTime.split(':')[0]);
    const attendeeEndHour = parseInt(attendeeDay.endTime.split(':')[0]);
    if (slotHour < userStartHour || slotHour >= userEndHour) return 'unavailable';
    if (slotHour < attendeeStartHour || slotHour >= attendeeEndHour) return 'unavailable';
    const userBlocked = userDay.blockedSlots?.includes(slot);
    const attendeeBlocked = attendeeDay.blockedSlots?.includes(slot);
    if (userBlocked && attendeeBlocked) return 'unavailable';
    if (userBlocked) return 'attendee-only';
    if (attendeeBlocked) return 'user-only';
    return 'both-available';
  };
  const handleRequestMeeting = () => {
    if (!selectedAttendee || !selectedSlot || !selectedLocation) return;
    const newMeeting: Meeting = {
      id: Date.now().toString(),
      attendeeId: selectedAttendee.id,
      attendeeName: selectedAttendee.name,
      attendeeCompany: selectedAttendee.company,
      attendeeRole: selectedAttendee.role,
      date: selectedDate,
      startTime: selectedSlot,
      endTime: `${parseInt(selectedSlot.split(':')[0])}:${parseInt(selectedSlot.split(':')[1]) + 30}`,
      location: selectedLocation,
      status: 'pending',
      requestedBy: 'me'
    };
    addMeeting(newMeeting);
    // If came from schedule, exit back to schedule
    if (cameFromSchedule && onNavigateToSchedule) {
      onNavigateToSchedule();
    } else {
      // Otherwise reset to main view
      setNavigationStack([{
        view: 'main'
      }]);
      setCurrentView('main');
    }
    setSelectedAttendee(null);
    setSelectedSlot(null);
    setSelectedLocation('');
    setSelectedTab('meetings');
  };
  const handleRescheduleMeeting = () => {
    if (!selectedMeeting || !selectedSlot) return;
    const newRequestedBy: 'me' | 'them' = selectedMeeting.requestedBy === 'them' ? 'me' : 'me';
    updateMeeting(selectedMeeting.id, {
      date: selectedDate,
      startTime: selectedSlot,
      endTime: `${parseInt(selectedSlot.split(':')[0])}:${parseInt(selectedSlot.split(':')[1]) + 30}`,
      status: 'pending',
      requestedBy: newRequestedBy
    });
    // If came from schedule, exit back to schedule
    if (cameFromSchedule && onNavigateToSchedule) {
      onNavigateToSchedule();
    } else {
      // Otherwise reset to main view
      setNavigationStack([{
        view: 'main'
      }]);
      setCurrentView('main');
    }
    setSelectedMeeting(null);
    setSelectedSlot(null);
    setSelectedTab('meetings');
  };
  const handleCancelMeeting = (meetingId: string) => {
    removeMeeting(meetingId);
    // If came from schedule, exit back to schedule
    if (cameFromSchedule && onNavigateToSchedule) {
      onNavigateToSchedule();
    } else {
      // Otherwise reset to main view
      setNavigationStack([{
        view: 'main'
      }]);
      setCurrentView('main');
    }
    setSelectedMeeting(null);
  };
  const handleAcceptMeeting = (meetingId: string) => {
    updateMeeting(meetingId, {
      status: 'confirmed'
    });
    // If came from schedule, exit back to schedule
    if (cameFromSchedule && onNavigateToSchedule) {
      onNavigateToSchedule();
    } else {
      // Otherwise reset to main view
      setNavigationStack([{
        view: 'main'
      }]);
      setCurrentView('main');
    }
    setSelectedMeeting(null);
  };
  const handleDeclineMeeting = (meetingId: string) => {
    removeMeeting(meetingId);
    // If came from schedule, exit back to schedule
    if (cameFromSchedule && onNavigateToSchedule) {
      onNavigateToSchedule();
    } else {
      // Otherwise reset to main view
      setNavigationStack([{
        view: 'main'
      }]);
      setCurrentView('main');
    }
    setSelectedMeeting(null);
  };
  const handleViewProfile = (attendee: Attendee) => {
    setSelectedAttendee(attendee);
    pushView('profile', {
      attendee
    });
  };
  const handleStartChat = (attendee: Attendee) => {
    const existingChat = chats.find(c => c.attendeeId === attendee.id);
    if (existingChat) {
      setSelectedChat(existingChat);
      pushView('chat', {
        chat: existingChat
      });
    } else {
      const newChat: ChatConversation = {
        id: `chat-${Date.now()}`,
        attendeeId: attendee.id,
        attendeeName: attendee.name,
        attendeeRole: attendee.role,
        attendeeCompany: attendee.company,
        attendeeImage: attendee.profileImage,
        lastMessage: '',
        lastMessageTime: 'Just now',
        unreadCount: 0,
        messages: []
      };
      setChats([newChat, ...chats]);
      setSelectedChat(newChat);
      pushView('chat', {
        chat: newChat
      });
    }
  };
  const handleRequestMeetingClick = (attendee: Attendee) => {
    setSelectedAttendee(attendee);
    setSelectedLocation('');
    pushView('meeting', {
      attendee
    });
  };
  const handleViewMeetingDetails = (meeting: Meeting) => {
    setSelectedMeeting(meeting);
    const attendeeFromMeeting: Attendee = {
      id: meeting.attendeeId,
      name: meeting.attendeeName,
      role: meeting.attendeeRole,
      company: meeting.attendeeCompany,
      interests: [],
      source: 'meeting'
    };
    setSelectedAttendee(attendeeFromMeeting);
    pushView('meetingDetails', {
      meeting,
      attendee: attendeeFromMeeting
    });
  };
  const handleSendMessage = () => {
    if (!messageInput.trim() || !selectedChat) return;
    const newMessage: ChatMessage = {
      id: `msg-${Date.now()}`,
      senderId: 'me',
      text: messageInput,
      timestamp: new Date().toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      }),
      isMe: true
    };
    const updatedChat = {
      ...selectedChat,
      messages: [...selectedChat.messages, newMessage],
      lastMessage: messageInput,
      lastMessageTime: 'Just now'
    };
    setChats(chats.map(c => c.id === selectedChat.id ? updatedChat : c));
    setSelectedChat(updatedChat);
    setMessageInput('');
    const responseDelay = Math.random() * 1000 + 2000;
    setTimeout(() => {
      const responses = ["That sounds great! I'd love to discuss this further.", 'Thanks for reaching out! Looking forward to connecting.', "Absolutely! Let's schedule some time to chat.", "Great idea! I'm interested in learning more.", "Thanks for the message! I'll get back to you soon.", 'Sounds good! Let me know what works for you.', "I appreciate you connecting! Let's definitely talk.", 'Perfect timing! I was hoping to discuss this.', "Thanks! I'm excited about the opportunity to connect.", 'That would be wonderful! Looking forward to it.'];
      const randomResponse = responses[Math.floor(Math.random() * responses.length)];
      const responseMessage: ChatMessage = {
        id: `msg-${Date.now()}`,
        senderId: selectedChat.attendeeId,
        text: randomResponse,
        timestamp: new Date().toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit'
        }),
        isMe: false
      };
      setSelectedChat(prevChat => {
        if (!prevChat || prevChat.id !== selectedChat.id) return prevChat;
        return {
          ...prevChat,
          messages: [...prevChat.messages, responseMessage],
          lastMessage: randomResponse,
          lastMessageTime: 'Just now'
        };
      });
      setChats(prevChats => prevChats.map(c => c.id === selectedChat.id ? {
        ...c,
        messages: [...c.messages, responseMessage],
        lastMessage: randomResponse,
        lastMessageTime: 'Just now'
      } : c));
    }, responseDelay);
  };
  const handleOpenChat = (chat: ChatConversation) => {
    const updatedChat = {
      ...chat,
      unreadCount: 0
    };
    setChats(chats.map(c => c.id === chat.id ? updatedChat : c));
    setSelectedChat(updatedChat);
    pushView('chat', {
      chat: updatedChat
    });
  };
  // Get meeting status label
  const getMeetingStatusLabel = (meeting: Meeting) => {
    if (meeting.status === 'confirmed') return 'Confirmed';
    if (meeting.requestedBy === 'me') return 'Awaiting Response';
    return 'Respond to Invite';
  };
  // Get meeting status badge variant
  const getMeetingStatusVariant = (meeting: Meeting): 'success' | 'warning' | 'default' => {
    if (meeting.status === 'confirmed') return 'success';
    if (meeting.requestedBy === 'me') return 'warning';
    return 'default';
  };
  // Add day filter options
  const days = [{
    id: 'all',
    label: 'All Days',
    date: ''
  }, {
    id: 'day1',
    label: 'Day 1',
    date: '24 Oct'
  }, {
    id: 'day2',
    label: 'Day 2',
    date: '25 Oct'
  }, {
    id: 'day3',
    label: 'Day 3',
    date: '26 Oct'
  }];
  // Helper to get day from date
  const getSessionDay = (dateStr: string): string => {
    if (dateStr.includes('October 24')) return 'day1';
    if (dateStr.includes('October 25')) return 'day2';
    if (dateStr.includes('October 26')) return 'day3';
    return 'day1';
  };
  // Filter meetings by day
  const filteredMeetingsByDay = useMemo(() => {
    if (selectedDay === 'all') return meetings;
    return meetings.filter(meeting => getSessionDay(meeting.date) === selectedDay);
  }, [meetings, selectedDay]);
  // Render sub-pages
  if (currentView === 'profile' && selectedAttendee) {
    return <div className="pb-24" data-id="element-3729">
        <div className="p-4 space-y-4" data-id="element-3730">
          {/* Profile Header */}
          <div className="text-center" data-id="element-3731">
            {selectedAttendee.profileImage ? <img src={selectedAttendee.profileImage} alt={selectedAttendee.name} className="w-24 h-24 rounded-full object-cover mx-auto mb-3" data-id="element-3732" /> : <div className="w-24 h-24 rounded-full flex items-center justify-center text-white text-2xl font-bold font-gotham mx-auto mb-3" style={{
            backgroundColor: currentEvent.colors.primary
          }} data-id="element-3733">
                {selectedAttendee.name.split(' ').map(n => n[0]).join('')}
              </div>}
            <h3 className="text-xl font-bold font-gotham mb-1" data-id="element-3734">
              {selectedAttendee.name}
            </h3>
            <p className="text-gray-600 font-gotham" data-id="element-3735">
              {selectedAttendee.role}
              {selectedAttendee.company && ` at ${selectedAttendee.company}`}
            </p>
            {selectedAttendee.matchScore && <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold font-gotham mt-2" style={{
            backgroundColor: `${currentEvent.colors.primary}20`,
            color: currentEvent.colors.primary
          }} data-id="element-3736">
                <StarIcon className="w-4 h-4 fill-current" data-id="element-3737" />
                {selectedAttendee.matchScore}% Match
              </div>}
          </div>

          {/* Contact Info */}
          {selectedAttendee.email && <Card padding="md" data-id="element-3738">
              <div className="flex items-center gap-3" data-id="element-3739">
                <MailIcon className="w-5 h-5 text-gray-400" data-id="element-3740" />
                <div data-id="element-3741">
                  <p className="text-xs text-gray-500 font-gotham" data-id="element-3742">Email</p>
                  <p className="text-sm font-gotham" data-id="element-3743">
                    {selectedAttendee.email}
                  </p>
                </div>
              </div>
            </Card>}

          {/* Seniority */}
          <Card padding="md" data-id="element-3744">
            <h4 className="font-bold font-gotham mb-2" data-id="element-3745">Seniority</h4>
            <Badge variant="default" size="md" data-id="element-3746">
              {getSeniorityFromTitle(selectedAttendee.role || '')}
            </Badge>
          </Card>

          {/* Responsibilities */}
          <Card padding="md" data-id="element-3747">
            <h4 className="font-bold font-gotham mb-2" data-id="element-3748">Responsibilities</h4>
            <Badge variant="default" size="md" data-id="element-3749">
              {getResponsibilityFromTitle(selectedAttendee.role || '')}
            </Badge>
          </Card>

          {/* Interests */}
          {selectedAttendee.interests.length > 0 && <Card padding="md" data-id="element-3750">
              <h4 className="font-bold font-gotham mb-2" data-id="element-3751">Interests</h4>
              <div className="flex flex-wrap gap-2" data-id="element-3752">
                {selectedAttendee.interests.map((interest, idx) => <Badge key={idx} variant="default" size="sm" data-id="element-3753">
                    {interest}
                  </Badge>)}
              </div>
            </Card>}

          {/* Actions */}
          <div className="flex gap-3" data-id="element-3754">
            <Button variant="primary" fullWidth onClick={() => handleRequestMeetingClick(selectedAttendee)} data-id="element-3755">
              <CalendarIcon className="w-4 h-4 mr-1 inline" data-id="element-3756" />
              Meet
            </Button>
            <Button variant="outline" fullWidth onClick={() => handleStartChat(selectedAttendee)} data-id="element-3757">
              <MessageCircleIcon className="w-4 h-4 mr-1 inline" data-id="element-3758" />
              Chat
            </Button>
          </div>
        </div>
      </div>;
  }
  if (currentView === 'chat' && selectedChat) {
    return <div className="pb-24 flex flex-col h-screen" data-id="element-3759">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-3760">
          {selectedChat.messages.length === 0 ? <div className="text-center py-12" data-id="element-3761">
              <MessageCircleIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" data-id="element-3762" />
              <p className="text-sm text-gray-600 font-gotham" data-id="element-3763">
                Start the conversation
              </p>
            </div> : selectedChat.messages.map(message => <div key={message.id} className={`flex ${message.isMe ? 'justify-end' : 'justify-start'}`} data-id="element-3764">
                <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${message.isMe ? 'rounded-br-sm' : 'rounded-bl-sm'}`} style={message.isMe ? {
            backgroundColor: currentEvent.colors.primary,
            color: 'white'
          } : {
            backgroundColor: '#f3f4f6',
            color: '#1f2937'
          }} data-id="element-3765">
                  <p className="text-sm font-gotham" data-id="element-3766">{message.text}</p>
                  <p className={`text-xs mt-1 font-gotham ${message.isMe ? 'text-white text-opacity-70' : 'text-gray-500'}`} data-id="element-3767">
                    {message.timestamp}
                  </p>
                </div>
              </div>)}
        </div>

        {/* Message Input */}
        <div className="p-4 border-t border-gray-200 bg-white" data-id="element-3768">
          <div className="flex gap-2" data-id="element-3769">
            <Input type="text" placeholder="Type a message..." value={messageInput} onChange={e => setMessageInput(e.target.value)} onKeyPress={e => {
            if (e.key === 'Enter') {
              handleSendMessage();
            }
          }} className="flex-1" data-id="element-3770" />
            <Button variant="primary" onClick={handleSendMessage} disabled={!messageInput.trim()} data-id="element-3771">
              <SendIcon className="w-5 h-5" data-id="element-3772" />
            </Button>
          </div>
        </div>
      </div>;
  }
  if (currentView === 'meetingDetails' && selectedMeeting && selectedAttendee) {
    return <div className="pb-24" data-id="element-3773">
        <div className="p-4 space-y-4" data-id="element-3774">
          <Card padding="md" data-id="element-3775">
            <div className="flex items-center gap-3" data-id="element-3776">
              {selectedAttendee.profileImage ? <img src={selectedAttendee.profileImage} alt={selectedAttendee.name} className="w-16 h-16 rounded-full object-cover" data-id="element-3777" /> : <div className="w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-bold font-gotham" style={{
              backgroundColor: currentEvent.colors.primary
            }} data-id="element-3778">
                  {selectedAttendee.name.split(' ').map(n => n[0]).join('')}
                </div>}
              <div className="flex-1" data-id="element-3779">
                <h4 className="font-bold font-gotham text-lg" data-id="element-3780">
                  {selectedAttendee.name}
                </h4>
                <p className="text-sm text-gray-600 font-gotham" data-id="element-3781">
                  {selectedAttendee.role}
                </p>
                {selectedAttendee.company && <p className="text-sm text-gray-600 font-gotham" data-id="element-3782">
                    {selectedAttendee.company}
                  </p>}
              </div>
              <button onClick={() => handleStartChat(selectedAttendee)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" style={{
              color: currentEvent.colors.primary
            }} data-id="element-3783">
                <MessageCircleIcon className="w-6 h-6" data-id="element-3784" />
              </button>
            </div>
          </Card>

          <Card padding="md" data-id="element-3785">
            <h4 className="font-bold font-gotham mb-3" data-id="element-3786">Meeting Information</h4>
            <div className="space-y-3" data-id="element-3787">
              <div className="flex items-start gap-3" data-id="element-3788">
                <div className="w-5 h-5 mt-0.5 flex items-center justify-center" style={{
                color: currentEvent.colors.primary
              }} data-id="element-3789">
                  <div className="w-2 h-2 rounded-full bg-current" data-id="element-3790"></div>
                </div>
                <div data-id="element-3791">
                  <p className="text-xs text-gray-500 font-gotham" data-id="element-3792">Status</p>
                  <Badge variant={getMeetingStatusVariant(selectedMeeting)} size="sm" data-id="element-3793">
                    {getMeetingStatusLabel(selectedMeeting)}
                  </Badge>
                </div>
              </div>

              <div className="flex items-start gap-3" data-id="element-3794">
                <CalendarIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-3795" />
                <div data-id="element-3796">
                  <p className="text-xs text-gray-500 font-gotham" data-id="element-3797">Date</p>
                  <p className="text-sm font-gotham" data-id="element-3798">
                    {new Date(selectedMeeting.date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3" data-id="element-3799">
                <ClockIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-3800" />
                <div data-id="element-3801">
                  <p className="text-xs text-gray-500 font-gotham" data-id="element-3802">Time</p>
                  <p className="text-sm font-gotham" data-id="element-3803">
                    {selectedMeeting.startTime} - {selectedMeeting.endTime}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3" data-id="element-3804">
                <MapPinIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-3805" />
                <div data-id="element-3806">
                  <p className="text-xs text-gray-500 font-gotham" data-id="element-3807">Location</p>
                  <p className="text-sm font-gotham" data-id="element-3808">
                    {selectedMeeting.location}
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {selectedMeeting.status === 'confirmed' && <div className="space-y-2" data-id="element-3809">
              <Button variant="outline" fullWidth onClick={() => {
            setSelectedDate(selectedMeeting.date);
            pushView('reschedule', {
              meeting: selectedMeeting,
              attendee: selectedAttendee
            });
          }} data-id="element-3810">
                <RefreshCwIcon className="w-4 h-4 mr-2 inline" data-id="element-3811" />
                Reschedule
              </Button>
              <Button variant="secondary" fullWidth onClick={() => handleCancelMeeting(selectedMeeting.id)} data-id="element-3812">
                <XIcon className="w-4 h-4 mr-2 inline" data-id="element-3813" />
                Cancel Meeting
              </Button>
            </div>}

          {selectedMeeting.status === 'pending' && selectedMeeting.requestedBy === 'me' && <div className="space-y-2" data-id="element-3814">
                <Button variant="outline" fullWidth onClick={() => {
            setSelectedDate(selectedMeeting.date);
            pushView('reschedule', {
              meeting: selectedMeeting,
              attendee: selectedAttendee
            });
          }} data-id="element-3815">
                  <RefreshCwIcon className="w-4 h-4 mr-2 inline" data-id="element-3816" />
                  Change Request
                </Button>
                <Button variant="secondary" fullWidth onClick={() => handleCancelMeeting(selectedMeeting.id)} data-id="element-3817">
                  <XIcon className="w-4 h-4 mr-2 inline" data-id="element-3818" />
                  Cancel Request
                </Button>
              </div>}

          {selectedMeeting.status === 'pending' && selectedMeeting.requestedBy === 'them' && <div className="space-y-2" data-id="element-3819">
                <Button variant="primary" fullWidth onClick={() => handleAcceptMeeting(selectedMeeting.id)} data-id="element-3820">
                  <CheckIcon className="w-4 h-4 mr-2 inline" data-id="element-3821" />
                  Accept Invite
                </Button>
                <Button variant="outline" fullWidth onClick={() => {
            setSelectedDate(selectedMeeting.date);
            pushView('reschedule', {
              meeting: selectedMeeting,
              attendee: selectedAttendee
            });
          }} data-id="element-3822">
                  <RefreshCwIcon className="w-4 h-4 mr-2 inline" data-id="element-3823" />
                  Suggest Alternative Time
                </Button>
                <Button variant="secondary" fullWidth onClick={() => handleDeclineMeeting(selectedMeeting.id)} data-id="element-3824">
                  <XIcon className="w-4 h-4 mr-2 inline" data-id="element-3825" />
                  Decline Invite
                </Button>
              </div>}
        </div>
      </div>;
  }
  if (currentView === 'reschedule' && selectedMeeting && selectedAttendee) {
    return <div className="pb-24" data-id="element-3826">
        <div className="p-4 space-y-4" data-id="element-3827">
          <Card padding="md" data-id="element-3828">
            <div className="flex items-center gap-3" data-id="element-3829">
              {selectedAttendee.profileImage ? <img src={selectedAttendee.profileImage} alt={selectedAttendee.name} className="w-12 h-12 rounded-full object-cover" data-id="element-3830" /> : <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold font-gotham" style={{
              backgroundColor: currentEvent.colors.primary
            }} data-id="element-3831">
                  {selectedAttendee.name.split(' ').map(n => n[0]).join('')}
                </div>}
              <div data-id="element-3832">
                <h4 className="font-bold font-gotham" data-id="element-3833">
                  {selectedAttendee.name}
                </h4>
                <p className="text-sm text-gray-600 font-gotham" data-id="element-3834">
                  {selectedAttendee.role}{' '}
                  {selectedAttendee.company && `at ${selectedAttendee.company}`}
                </p>
              </div>
            </div>
          </Card>

          <div data-id="element-3835">
            <label className="block text-sm font-semibold text-gray-700 mb-2 font-gotham" data-id="element-3836">
              Select New Date
            </label>
            <div className="grid grid-cols-3 gap-2" data-id="element-3837">
              {eventDates.map(date => <button key={date} onClick={() => {
              setSelectedDate(date);
              setSelectedSlot(null);
            }} className={`p-3 rounded-lg border-2 text-sm font-medium font-gotham transition-all ${selectedDate === date ? 'border-current shadow-sm' : 'border-gray-200 hover:border-gray-300'}`} style={selectedDate === date ? {
              color: currentEvent.colors.primary,
              borderColor: currentEvent.colors.primary
            } : {}} data-id="element-3838">
                  {new Date(date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
              })}
                </button>)}
            </div>
          </div>

          <div data-id="element-3839">
            <label className="block text-sm font-semibold text-gray-700 mb-2 font-gotham" data-id="element-3840">
              Select New Time (30-minute slots)
            </label>
            <div className="grid grid-cols-4 gap-2 max-h-64 overflow-y-auto" data-id="element-3841">
              {timeSlots.map(slot => {
              const availability = getSlotAvailability(selectedDate, slot);
              const isDisabled = availability === 'unavailable';
              const isBothAvailable = availability === 'both-available';
              return <button key={slot} onClick={() => !isDisabled && setSelectedSlot(slot)} disabled={isDisabled} className={`p-2 rounded-lg text-sm font-medium font-gotham transition-all ${selectedSlot === slot ? 'ring-2 ring-offset-2' : ''} ${isDisabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : isBothAvailable ? 'bg-green-50 text-green-700 hover:bg-green-100 border-2 border-green-200' : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border-2 border-yellow-200'}`} style={selectedSlot === slot ? {
                '--tw-ring-color': currentEvent.colors.primary
              } as React.CSSProperties : {}} data-id="element-3842">
                    {slot}
                  </button>;
            })}
            </div>

            <div className="mt-3 flex flex-wrap gap-3 text-xs font-gotham" data-id="element-3843">
              <div className="flex items-center gap-1" data-id="element-3844">
                <div className="w-3 h-3 rounded bg-green-200" data-id="element-3845"></div>
                <span data-id="element-3846">Both available</span>
              </div>
              <div className="flex items-center gap-1" data-id="element-3847">
                <div className="w-3 h-3 rounded bg-yellow-200" data-id="element-3848"></div>
                <span data-id="element-3849">Partial availability</span>
              </div>
              <div className="flex items-center gap-1" data-id="element-3850">
                <div className="w-3 h-3 rounded bg-gray-200" data-id="element-3851"></div>
                <span data-id="element-3852">Unavailable</span>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-2" data-id="element-3853">
            <Button variant="outline" fullWidth onClick={handleInternalBack} data-id="element-3854">
              Cancel
            </Button>
            <Button variant="primary" fullWidth onClick={handleRescheduleMeeting} disabled={!selectedSlot} data-id="element-3855">
              Reschedule
            </Button>
          </div>
        </div>
      </div>;
  }
  if (currentView === 'meeting' && selectedAttendee) {
    return <div className="pb-24" data-id="element-3856">
        <div className="p-4 space-y-4" data-id="element-3857">
          <Card padding="md" data-id="element-3858">
            <div className="flex items-center gap-3" data-id="element-3859">
              {selectedAttendee.profileImage ? <img src={selectedAttendee.profileImage} alt={selectedAttendee.name} className="w-12 h-12 rounded-full object-cover" data-id="element-3860" /> : <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold font-gotham" style={{
              backgroundColor: currentEvent.colors.primary
            }} data-id="element-3861">
                  {selectedAttendee.name.split(' ').map(n => n[0]).join('')}
                </div>}
              <div data-id="element-3862">
                <h4 className="font-bold font-gotham" data-id="element-3863">
                  {selectedAttendee.name}
                </h4>
                <p className="text-sm text-gray-600 font-gotham" data-id="element-3864">
                  {selectedAttendee.role}{' '}
                  {selectedAttendee.company && `at ${selectedAttendee.company}`}
                </p>
              </div>
            </div>
          </Card>

          <div data-id="element-3865">
            <label className="block text-sm font-semibold text-gray-700 mb-2 font-gotham" data-id="element-3866">
              Select Date
            </label>
            <div className="grid grid-cols-3 gap-2" data-id="element-3867">
              {eventDates.map(date => <button key={date} onClick={() => {
              setSelectedDate(date);
              setSelectedSlot(null);
            }} className={`p-3 rounded-lg border-2 text-sm font-medium font-gotham transition-all ${selectedDate === date ? 'border-current shadow-sm' : 'border-gray-200 hover:border-gray-300'}`} style={selectedDate === date ? {
              color: currentEvent.colors.primary,
              borderColor: currentEvent.colors.primary
            } : {}} data-id="element-3868">
                  {new Date(date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
              })}
                </button>)}
            </div>
          </div>

          <div data-id="element-3869">
            <label className="block text-sm font-semibold text-gray-700 mb-2 font-gotham" data-id="element-3870">
              Select Time (30-minute slots)
            </label>
            <div className="grid grid-cols-4 gap-2 max-h-64 overflow-y-auto" data-id="element-3871">
              {timeSlots.map(slot => {
              const availability = getSlotAvailability(selectedDate, slot);
              const isDisabled = availability === 'unavailable';
              const isBothAvailable = availability === 'both-available';
              return <button key={slot} onClick={() => !isDisabled && setSelectedSlot(slot)} disabled={isDisabled} className={`p-2 rounded-lg text-sm font-medium font-gotham transition-all ${selectedSlot === slot ? 'ring-2 ring-offset-2' : ''} ${isDisabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : isBothAvailable ? 'bg-green-50 text-green-700 hover:bg-green-100 border-2 border-green-200' : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border-2 border-yellow-200'}`} style={selectedSlot === slot ? {
                '--tw-ring-color': currentEvent.colors.primary
              } as React.CSSProperties : {}} data-id="element-3872">
                    {slot}
                  </button>;
            })}
            </div>

            <div className="mt-3 flex flex-wrap gap-3 text-xs font-gotham" data-id="element-3873">
              <div className="flex items-center gap-1" data-id="element-3874">
                <div className="w-3 h-3 rounded bg-green-200" data-id="element-3875"></div>
                <span data-id="element-3876">Both available</span>
              </div>
              <div className="flex items-center gap-1" data-id="element-3877">
                <div className="w-3 h-3 rounded bg-yellow-200" data-id="element-3878"></div>
                <span data-id="element-3879">Partial availability</span>
              </div>
              <div className="flex items-center gap-1" data-id="element-3880">
                <div className="w-3 h-3 rounded bg-gray-200" data-id="element-3881"></div>
                <span data-id="element-3882">Unavailable</span>
              </div>
            </div>
          </div>

          <div className="mt-6" data-id="element-3883">
            <label className="block text-sm font-semibold text-gray-700 mb-2 font-gotham" data-id="element-3884">
              Select Meeting Location
            </label>
            <div className="space-y-2" data-id="element-3885">
              <button onClick={() => setSelectedLocation(`${selectedAttendee.company} Stand`)} className={`w-full p-3 rounded-lg border-2 text-left text-sm font-medium font-gotham transition-all ${selectedLocation === `${selectedAttendee.company} Stand` ? 'border-current shadow-sm' : 'border-gray-200 hover:border-gray-300'}`} style={selectedLocation === `${selectedAttendee.company} Stand` ? {
              color: currentEvent.colors.primary,
              borderColor: currentEvent.colors.primary,
              backgroundColor: `${currentEvent.colors.primary}10`
            } : {}} data-id="element-3886">
                <div className="flex items-center gap-2" data-id="element-3887">
                  <MapPinIcon className="w-4 h-4" data-id="element-3888" />
                  <span data-id="element-3889">{selectedAttendee.company} Stand</span>
                </div>
              </button>

              <button onClick={() => setSelectedLocation('My Company Stand')} className={`w-full p-3 rounded-lg border-2 text-left text-sm font-medium font-gotham transition-all ${selectedLocation === 'My Company Stand' ? 'border-current shadow-sm' : 'border-gray-200 hover:border-gray-300'}`} style={selectedLocation === 'My Company Stand' ? {
              color: currentEvent.colors.primary,
              borderColor: currentEvent.colors.primary,
              backgroundColor: `${currentEvent.colors.primary}10`
            } : {}} data-id="element-3890">
                <div className="flex items-center gap-2" data-id="element-3891">
                  <MapPinIcon className="w-4 h-4" data-id="element-3892" />
                  <span data-id="element-3893">My Company Stand</span>
                </div>
              </button>

              <button onClick={() => setSelectedLocation("Let's chat on the day")} className={`w-full p-3 rounded-lg border-2 text-left text-sm font-medium font-gotham transition-all ${selectedLocation === "Let's chat on the day" ? 'border-current shadow-sm' : 'border-gray-200 hover:border-gray-300'}`} style={selectedLocation === "Let's chat on the day" ? {
              color: currentEvent.colors.primary,
              borderColor: currentEvent.colors.primary,
              backgroundColor: `${currentEvent.colors.primary}10`
            } : {}} data-id="element-3894">
                <div className="flex items-center gap-2" data-id="element-3895">
                  <MessageCircleIcon className="w-4 h-4" data-id="element-3896" />
                  <span data-id="element-3897">Let's chat on the day</span>
                </div>
              </button>
            </div>
          </div>

          <div className="flex gap-3 pt-2" data-id="element-3898">
            <Button variant="outline" fullWidth onClick={handleInternalBack} data-id="element-3899">
              Cancel
            </Button>
            <Button variant="primary" fullWidth onClick={handleRequestMeeting} disabled={!selectedSlot || !selectedLocation} data-id="element-3900">
              Request
            </Button>
          </div>
        </div>
      </div>;
  }
  // Main view with tabs
  return <div className="pb-24" data-id="element-3901">
      {/* Tabs with Counts */}
      <div className="sticky top-[88px] z-30 bg-white border-b border-gray-200 px-4 pt-4" data-id="element-3902">
        <div className="flex gap-0 mb-4 -mx-4 px-4" data-id="element-3903">
          {[{
          id: 'matches',
          label: 'Matches',
          icon: StarIcon,
          count: topMatches.length
        }, {
          id: 'attendees',
          label: 'Attendees',
          icon: UsersIcon,
          count: attendees.length
        }, {
          id: 'meetings',
          label: 'Meetings',
          icon: CalendarIcon,
          count: meetings.length
        }, {
          id: 'chats',
          label: 'Chats',
          icon: MessageCircleIcon,
          count: chats.reduce((sum, chat) => sum + chat.unreadCount, 0)
        }].map(tab => {
          const Icon = tab.icon;
          const hasUnread = tab.id === 'chats' && tab.count > 0;
          return <button key={tab.id} onClick={() => setSelectedTab(tab.id as any)} className={`flex-1 flex flex-col items-center justify-center gap-1 px-2 py-3 font-medium font-gotham transition-all border-b-2 relative ${selectedTab === tab.id ? 'border-current' : 'border-transparent text-gray-600'}`} style={selectedTab === tab.id ? {
            color: currentEvent.colors.primary,
            borderColor: currentEvent.colors.primary
          } : {}} data-id="element-3904">
                <Icon className="w-5 h-5" data-id="element-3905" />
                <div className="flex flex-col items-center" data-id="element-3906">
                  <span className="text-lg font-bold" data-id="element-3907">
                    {tab.id === 'chats' ? chats.length : tab.count}
                  </span>
                  <span className="text-xs" data-id="element-3908">{tab.label}</span>
                </div>
                {hasUnread && <div className="absolute top-2 right-2 w-2 h-2 rounded-full" style={{
              backgroundColor: currentEvent.colors.primary
            }} data-id="element-3909" />}
              </button>;
        })}
        </div>

        {selectedTab === 'attendees' && <div className="pb-4 space-y-3" data-id="element-3910">
            <div className="relative" data-id="element-3911">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-3912" />
              <Input type="text" placeholder="Search attendees..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-id="element-3913" />
            </div>

            {/* Filter Toggle */}
            <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-2 px-4 py-2 rounded-lg border-2 text-sm font-medium font-gotham transition-all" style={{
          borderColor: currentEvent.colors.primary,
          color: currentEvent.colors.primary,
          backgroundColor: showFilters ? `${currentEvent.colors.primary}10` : 'transparent'
        }} data-id="element-3914">
              <FilterIcon className="w-4 h-4" data-id="element-3915" />
              Filters
              {(selectedSeniority !== 'all' || selectedInterest !== 'all' || selectedResponsibility !== 'all') && <Badge variant="warning" size="sm" data-id="element-3916">
                  Active
                </Badge>}
            </button>

            {/* Filter Controls */}
            {showFilters && <div className="space-y-3" data-id="element-3917">
                {/* Seniority Filter */}
                <div data-id="element-3918">
                  <label className="text-xs font-semibold text-gray-500 font-gotham mb-1 block" data-id="element-3919">
                    SENIORITY
                  </label>
                  <select value={selectedSeniority} onChange={e => setSelectedSeniority(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm font-gotham" data-id="element-3920">
                    {seniorityCategories.map(seniority => <option key={seniority} value={seniority} data-id="element-3921">
                        {seniority === 'all' ? 'All Seniority Levels' : seniority}
                      </option>)}
                  </select>
                </div>

                {/* Responsibility Filter */}
                <div data-id="element-3922">
                  <label className="text-xs font-semibold text-gray-500 font-gotham mb-1 block" data-id="element-3923">
                    RESPONSIBILITY
                  </label>
                  <select value={selectedResponsibility} onChange={e => setSelectedResponsibility(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm font-gotham" data-id="element-3924">
                    {responsibilityOptions.map(responsibility => <option key={responsibility} value={responsibility} data-id="element-3925">
                        {responsibility === 'all' ? 'All Responsibilities' : responsibility}
                      </option>)}
                  </select>
                </div>

                {/* Interest Filter */}
                <div data-id="element-3926">
                  <label className="text-xs font-semibold text-gray-500 font-gotham mb-1 block" data-id="element-3927">
                    INTERESTS
                  </label>
                  <select value={selectedInterest} onChange={e => setSelectedInterest(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm font-gotham" data-id="element-3928">
                    {interestOptions.map(interest => <option key={interest} value={interest} data-id="element-3929">
                        {interest === 'all' ? 'All Interests' : interest}
                      </option>)}
                  </select>
                </div>

                {/* Clear Filters */}
                {(selectedSeniority !== 'all' || selectedInterest !== 'all' || selectedResponsibility !== 'all') && <button onClick={() => {
            setSelectedSeniority('all');
            setSelectedInterest('all');
            setSelectedResponsibility('all');
          }} className="w-full py-2 text-sm font-medium font-gotham text-gray-600 hover:text-gray-900" data-id="element-3930">
                    Clear All Filters
                  </button>}
              </div>}
          </div>}
      </div>

      {/* Content */}
      <div className="px-4 py-4 pb-20" data-id="element-3931">
        {selectedTab === 'matches' && <div className="space-y-3" data-id="element-3932">
            {topMatches.map(match => <Card key={match.id} padding="md" data-id="element-3933">
                <div className="flex gap-4" data-id="element-3934">
                  {match.profileImage ? <img src={match.profileImage} alt={match.name} className="w-14 h-14 rounded-full object-cover flex-shrink-0" data-id="element-3935" /> : <div className="w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold font-gotham flex-shrink-0" style={{
              backgroundColor: currentEvent.colors.primary
            }} data-id="element-3936">
                      {match.name.split(' ').map(n => n[0]).join('')}
                    </div>}

                  <div className="flex-1 min-w-0" data-id="element-3937">
                    <div className="flex items-start justify-between gap-2 mb-1" data-id="element-3938">
                      <h3 className="font-bold text-gray-900 font-gotham" data-id="element-3939">
                        {match.name}
                      </h3>
                      {match.matchScore && <div className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold font-gotham flex-shrink-0" style={{
                  backgroundColor: `${currentEvent.colors.primary}20`,
                  color: currentEvent.colors.primary
                }} data-id="element-3940">
                          <StarIcon className="w-3 h-3 fill-current" data-id="element-3941" />
                          {match.matchScore}%
                        </div>}
                    </div>

                    <div className="flex items-center gap-2 text-sm text-gray-600 font-gotham mb-2" data-id="element-3942">
                      <BriefcaseIcon className="w-4 h-4" data-id="element-3943" />
                      {match.role} {match.company && `at ${match.company}`}
                    </div>

                    {match.interests.length > 0 && <div className="flex flex-wrap gap-1 mb-3" data-id="element-3944">
                        {match.interests.slice(0, 3).map((interest, idx) => <Badge key={idx} variant="default" size="sm" data-id="element-3945">
                            {interest}
                          </Badge>)}
                      </div>}

                    <div className="grid grid-cols-3 gap-2" data-id="element-3946">
                      <Button variant="outline" size="sm" onClick={() => handleViewProfile(match)} className="flex items-center justify-center gap-1" data-id="element-3947">
                        <UserIcon className="w-4 h-4" data-id="element-3948" />
                        <span data-id="element-3949">View</span>
                      </Button>
                      <Button variant="primary" size="sm" onClick={() => handleRequestMeetingClick(match)} className="flex items-center justify-center gap-1" data-id="element-3950">
                        <CalendarIcon className="w-4 h-4" data-id="element-3951" />
                        <span data-id="element-3952">Meet</span>
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleStartChat(match)} className="flex items-center justify-center gap-1" data-id="element-3953">
                        <MessageCircleIcon className="w-4 h-4" data-id="element-3954" />
                        <span data-id="element-3955">Chat</span>
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>)}
          </div>}

        {selectedTab === 'attendees' && <div className="space-y-3" data-id="element-3956">
            {filteredAttendees.length === 0 ? <div className="text-center py-8 text-gray-500 font-gotham" data-id="element-3957">
                <UsersIcon className="w-12 h-12 mx-auto mb-3 opacity-50" data-id="element-3958" />
                <p data-id="element-3959">No attendees found</p>
              </div> : filteredAttendees.map(attendee => <Card key={attendee.id} padding="md" data-id="element-3960">
                  <div className="flex gap-4" data-id="element-3961">
                    {attendee.profileImage ? <img src={attendee.profileImage} alt={attendee.name} className="w-14 h-14 rounded-full object-cover flex-shrink-0" data-id="element-3962" /> : <div className="w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold font-gotham flex-shrink-0" style={{
              backgroundColor: currentEvent.colors.primary
            }} data-id="element-3963">
                        {attendee.name.split(' ').map(n => n[0]).join('')}
                      </div>}

                    <div className="flex-1 min-w-0" data-id="element-3964">
                      <h3 className="font-bold text-gray-900 font-gotham mb-1" data-id="element-3965">
                        {attendee.name}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-gray-600 font-gotham mb-2" data-id="element-3966">
                        <BriefcaseIcon className="w-4 h-4" data-id="element-3967" />
                        {attendee.role}{' '}
                        {attendee.company && `at ${attendee.company}`}
                      </div>

                      {attendee.interests.length > 0 && <div className="flex flex-wrap gap-1 mb-3" data-id="element-3968">
                          {attendee.interests.slice(0, 3).map((interest, idx) => <Badge key={idx} variant="default" size="sm" data-id="element-3969">
                                {interest}
                              </Badge>)}
                        </div>}

                      <div className="grid grid-cols-3 gap-2" data-id="element-3970">
                        <Button variant="outline" size="sm" onClick={() => handleViewProfile(attendee)} className="flex items-center justify-center gap-1" data-id="element-3971">
                          <UserIcon className="w-4 h-4" data-id="element-3972" />
                          <span data-id="element-3973">View</span>
                        </Button>
                        <Button variant="primary" size="sm" onClick={() => handleRequestMeetingClick(attendee)} className="flex items-center justify-center gap-1" data-id="element-3974">
                          <CalendarIcon className="w-4 h-4" data-id="element-3975" />
                          <span data-id="element-3976">Meet</span>
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleStartChat(attendee)} className="flex items-center justify-center gap-1" data-id="element-3977">
                          <MessageCircleIcon className="w-4 h-4" data-id="element-3978" />
                          <span data-id="element-3979">Chat</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>)}
          </div>}

        {selectedTab === 'meetings' && <>
            <div className="space-y-3" data-id="element-3980">
              {meetings.length === 0 ? <div className="text-center py-8 text-gray-500 font-gotham" data-id="element-3981">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-3 opacity-50" data-id="element-3982" />
                  <p data-id="element-3983">No meetings scheduled</p>
                </div> : (() => {
            // Sort meetings by date and time
            const sortedMeetings = [...meetings].sort((a, b) => {
              const dateCompare = new Date(a.date).getTime() - new Date(b.date).getTime();
              if (dateCompare !== 0) return dateCompare;
              return a.startTime.localeCompare(b.startTime);
            });
            // Group meetings by date
            const meetingsByDate = sortedMeetings.reduce((acc, meeting) => {
              if (!acc[meeting.date]) {
                acc[meeting.date] = [];
              }
              acc[meeting.date].push(meeting);
              return acc;
            }, {} as Record<string, typeof meetings>);
            return <div className="space-y-6" data-id="element-3984">
                      {Object.entries(meetingsByDate).map(([date, dateMeetings]) => <div key={date} data-id="element-3985">
                            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3 font-gotham" data-id="element-3986">
                              {new Date(date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric'
                  })}
                            </h3>
                            <div className="space-y-3" data-id="element-3987">
                              {dateMeetings.map(meeting => <Card key={meeting.id} padding="md" className="cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => handleViewMeetingDetails(meeting)} data-id="element-3988">
                                  <div className="flex items-start justify-between mb-3" data-id="element-3989">
                                    <div className="flex-1" data-id="element-3990">
                                      <h3 className="font-bold text-gray-900 font-gotham mb-1" data-id="element-3991">
                                        {meeting.attendeeName}
                                      </h3>
                                      {meeting.attendeeRole && <p className="text-sm text-gray-600 font-gotham mb-2" data-id="element-3992">
                                          {meeting.attendeeRole}{' '}
                                          {meeting.attendeeCompany && `at ${meeting.attendeeCompany}`}
                                        </p>}
                                      <div className="flex items-center gap-3 text-sm text-gray-600 font-gotham" data-id="element-3993">
                                        <span className="flex items-center gap-1" data-id="element-3994">
                                          <ClockIcon className="w-4 h-4" data-id="element-3995" />
                                          {meeting.startTime} -{' '}
                                          {meeting.endTime}
                                        </span>
                                      </div>
                                    </div>
                                    <Badge variant={getMeetingStatusVariant(meeting)} size="sm" data-id="element-3996">
                                      {getMeetingStatusLabel(meeting)}
                                    </Badge>
                                  </div>
                                </Card>)}
                            </div>
                          </div>)}
                    </div>;
          })()}
            </div>
          </>}

        {selectedTab === 'chats' && <div className="space-y-3" data-id="element-3997">
            {chats.length === 0 ? <div className="text-center py-8 text-gray-500 font-gotham" data-id="element-3998">
                <MessageCircleIcon className="w-12 h-12 mx-auto mb-3 opacity-50" data-id="element-3999" />
                <p data-id="element-4000">No conversations yet</p>
                <p className="text-sm mt-2" data-id="element-4001">
                  Start chatting with attendees to see your conversations here
                </p>
              </div> : chats.map(chat => <Card key={chat.id} padding="md" className="cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => handleOpenChat(chat)} data-id="element-4002">
                  <div className="flex gap-4" data-id="element-4003">
                    {chat.attendeeImage ? <img src={chat.attendeeImage} alt={chat.attendeeName} className="w-14 h-14 rounded-full object-cover flex-shrink-0" data-id="element-4004" /> : <div className="w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold font-gotham flex-shrink-0" style={{
              backgroundColor: currentEvent.colors.primary
            }} data-id="element-4005">
                        {chat.attendeeName.split(' ').map(n => n[0]).join('')}
                      </div>}

                    <div className="flex-1 min-w-0" data-id="element-4006">
                      <div className="flex items-start justify-between gap-2 mb-1" data-id="element-4007">
                        <h3 className="font-bold text-gray-900 font-gotham" data-id="element-4008">
                          {chat.attendeeName}
                        </h3>
                        <span className="text-xs text-gray-500 font-gotham flex-shrink-0" data-id="element-4009">
                          {chat.lastMessageTime}
                        </span>
                      </div>

                      {chat.attendeeRole && <p className="text-xs text-gray-600 font-gotham mb-1" data-id="element-4010">
                          {chat.attendeeRole}
                          {chat.attendeeCompany && ` at ${chat.attendeeCompany}`}
                        </p>}

                      <div className="flex items-center justify-between gap-2" data-id="element-4011">
                        <p className="text-sm text-gray-600 font-gotham truncate" data-id="element-4012">
                          {chat.lastMessage || 'No messages yet'}
                        </p>
                        {chat.unreadCount > 0 && <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold font-gotham" style={{
                  backgroundColor: currentEvent.colors.primary
                }} data-id="element-4013">
                            {chat.unreadCount}
                          </div>}
                      </div>
                    </div>
                  </div>
                </Card>)}
          </div>}
      </div>

      {/* Sticky My Schedule Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 z-40" data-id="element-4014">
        <Button variant="primary" fullWidth onClick={() => {
        // Simply call the parent's navigation handler
        // This will properly navigate to My Schedule
        if (onNavigateToSchedule) {
          onNavigateToSchedule();
        }
      }} data-id="element-4015">
          <CalendarDaysIcon className="w-5 h-5 mr-2 inline" data-id="element-4016" />
          My Schedule
        </Button>
      </div>
    </div>;
}